import { useEffect, useRef, useState } from 'react';

import { Button } from './button';

type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost';
type ButtonSize = 'sm' | 'md' | 'lg';

interface ConfirmButtonProps {
  onConfirm: () => void;
  children: React.ReactNode;
  confirmText?: string;
  armedMs?: number;
  variant?: ButtonVariant;
  size?: ButtonSize;
  disabled?: boolean;
  className?: string;
}

// Two-click confirm for destructive actions, without pulling in a modal
// dependency: first click arms it (button flips to danger + confirmText),
// a second click within `armedMs` fires onConfirm. Anything else (timeout,
// re-render) disarms it.
const ConfirmButton = ({
  onConfirm,
  children,
  confirmText = 'Confirm?',
  armedMs = 3000,
  variant = 'secondary',
  size = 'md',
  disabled,
  className,
}: ConfirmButtonProps) => {
  const [armed, setArmed] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  const handleClick = (): void => {
    if (!armed) {
      setArmed(true);
      timerRef.current = setTimeout(() => setArmed(false), armedMs);
      return;
    }
    clearTimeout(timerRef.current);
    setArmed(false);
    onConfirm();
  };

  return (
    <Button
      type="button"
      variant={armed ? 'danger' : variant}
      size={size}
      disabled={disabled}
      onClick={handleClick}
      className={className}
      // The idle className often sets a custom text color (e.g. text-danger,
      // for a "delete" link look). Once armed, the button itself turns danger-
      // red, so that same text color override would render unreadable
      // (red-on-red) - textClassName always wins over className on Button,
      // so this forces it back to white only while armed.
      textClassName={armed ? 'text-white hover:text-white' : undefined}
    >
      {armed ? confirmText : children}
    </Button>
  );
};

export default ConfirmButton;
